/**
 * useEvent Hook
 *
 * Fetches a single event by ID using URQL.
 */

import { useQuery } from "urql";
import type { Event } from "@/generated/graphql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const EventDocument = `
  query Event($id: ID!, $preview: Boolean) {
    event(id: $id, preview: $preview) {
      id
      title
      subtitle
      description
      venue
      address
      mapsUrl
      startsAt
      endsAt
      imageUrl
      videoUrl
      categories {
        id
        name
      }
      streamYoutubeUrl
      streamVkUrl
      telegramChatUrl
      inviteText
      slogan
      program
      isActive
      requiresRsvpConfirmation
      hasMyTickets
      myRsvp {
        id
        status
      }
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
        feesBreakdown {
          name
          type
          amount
        }
      }
      homeGroup {
        id
        name
      }
      activeRaffle {
        id
        name
        status
      }
      activeGenderParty {
        id
        name
        status
      }
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseEventOptions {
  /** If true, shows unpublished events (for admin preview) */
  preview?: boolean;
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseEventResult {
  event: Event | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch a single event by ID.
 *
 * Uses URQL for normalized caching and automatic refetch on cache invalidation.
 */
export function useEvent(
  eventId: string | undefined,
  options: UseEventOptions = {}
): UseEventResult {
  const { preview = false, pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ event: Event | null }>({
    query: EventDocument,
    variables: { id: eventId, preview },
    pause: pause || !eventId,
  });

  return {
    event: result.data?.event ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

